import React, { useEffect, useState } from 'react';
import { nps as npsApi } from '@/utils/api';
import { Sparkles, X, Send, CheckCircle2, MailQuestion, Clock } from 'lucide-react';
import { toast } from 'sonner';

/**
 * RequestFeedbackModal — operator decides whether to send the NPS+metrics+benefits
 * feedback request right after marking a project complete.
 *
 * Two surfaces:
 *  - "Send now" → calls POST /api/nps/projects/{id}/request and closes
 *  - "Maybe later" → just closes; the parent UI keeps showing a "Request feedback"
 *    button until the operator either sends or the client answers.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   project: { id, name, status, client_id }
 *   onSent?: (invite) => void  // optional callback after successful send
 */
export default function RequestFeedbackModal({ open, onClose, project, onSent }) {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    if (!open || !project?.id) return;
    setLoadingStatus(true);
    npsApi.status(project.id)
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ can_request: true, is_sent: false }))
      .finally(() => setLoadingStatus(false));
  }, [open, project?.id]);

  const send = async () => {
    setSending(true);
    try {
      const r = await npsApi.request(project.id);
      if (r.data.already_sent) {
        toast.info(r.data.message || 'Already pending');
      } else {
        toast.success('Feedback request sent to client');
      }
      if (onSent) onSent(r.data.invite);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send — check SMTP / brand email setup.');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={onClose}
      data-testid="request-feedback-modal"
    >
      <div
        className="card-base w-full max-w-lg p-6 animate-fade-in"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: '#5B2D8E20' }}>
              <Sparkles size={18} style={{ color: '#5B2D8E' }} />
            </div>
            <div>
              <h3 className="font-black text-lg leading-tight" style={{ color: 'var(--ink)' }}>
                Request client feedback?
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                {project?.name || 'This project'} just hit a closing status.
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ color: 'var(--muted)' }} data-testid="rfm-close"><X size={16} /></button>
        </div>

        {loadingStatus ? (
          <div className="py-8 text-center text-sm" style={{ color: 'var(--muted)' }}>Loading…</div>
        ) : status?.is_answered ? (
          <div className="card-base p-4 text-center" style={{ background: '#3C8A5A10' }}>
            <CheckCircle2 size={28} className="mx-auto mb-2" style={{ color: '#3C8A5A' }} />
            <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Already answered</p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
              Score {status.answered?.score} · {new Date(status.answered?.answered_at).toLocaleDateString()}
            </p>
          </div>
        ) : status?.is_sent ? (
          <div className="card-base p-4 text-center" style={{ background: '#D9A44115' }}>
            <Clock size={28} className="mx-auto mb-2" style={{ color: '#8A6B1A' }} />
            <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Already sent</p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
              Waiting on the client to answer. Sent {new Date(status.pending?.sent_at).toLocaleString()}.
            </p>
          </div>
        ) : (
          <>
            <div className="card-base p-4 mb-4" style={{ background: 'var(--bg)' }}>
              <p className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
                <MailQuestion size={13} /> The client will be asked for:
              </p>
              <ul className="text-xs space-y-1.5" style={{ color: 'var(--muted)' }}>
                <li>• <b>NPS score</b> (0–10) — likelihood to recommend</li>
                <li>• <b>Metric improved</b> — concrete number that moved (e.g. "12h/wk → 30 min")</li>
                <li>• <b>Biggest benefit</b> — qualitative win for their team / business</li>
                <li>• <b>Free comment</b> + optional consent to publish as a public testimonial</li>
              </ul>
            </div>

            <p className="text-xs mb-4" style={{ color: 'var(--muted)' }}>
              Sent via your brand SMTP (with agency fallback). Won't fire automatically — you decide when.
              You can also click "Request feedback" later from the project page.
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 py-2.5 text-sm rounded-btn font-semibold border"
                style={{ borderColor: 'var(--border)', color: 'var(--muted)', background: 'transparent' }}
                onClick={onClose}
                data-testid="rfm-skip"
              >
                Maybe later
              </button>
              <button
                type="button"
                className="flex-1 btn-primary py-2.5 text-sm flex items-center justify-center gap-1.5"
                onClick={send}
                disabled={sending || !status?.can_request}
                data-testid="rfm-send"
              >
                <Send size={13} /> {sending ? 'Sending…' : 'Send now'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
